'use client';

import React, { useState, useRef } from 'react';
import {
  Loader2,
  Upload,
  X,
  FileText,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Users,
} from 'lucide-react';
import {
  PDActivityCategory,
  CATEGORY_INFO,
  DIDACTIC_CATEGORIES,
  EXPERIENTIAL_CATEGORIES,
} from './types';

interface AddPDActivityFormProps {
  proId: string;
  apiEndpoint: string;
  crpoRegistrationDate?: string | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormState {
  title: string;
  category: PDActivityCategory | '';
  activityDate: string;
  durationHours: number;
  durationMinutes: number;
  provider: string;
  description: string;
  learningObjectives: string;
  keyTakeaways: string;
  applicationPlan: string;
}

export function AddPDActivityForm({
  proId,
  apiEndpoint,
  crpoRegistrationDate,
  onSuccess,
  onCancel,
}: AddPDActivityFormProps) {
  const [form, setForm] = useState<FormState>({
    title: '',
    category: '',
    activityDate: '',
    durationHours: 1,
    durationMinutes: 0,
    provider: '',
    description: '',
    learningObjectives: '',
    keyTakeaways: '',
    applicationPlan: '',
  });

  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReflection, setShowReflection] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleNumberChange = (name: 'durationHours' | 'durationMinutes', value: number) => {
    setForm((prev) => ({ ...prev, [name]: Math.max(0, value) }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      if (!allowedTypes.includes(file.type)) {
        setError('File must be PDF, JPEG, PNG, or Word document');
        return;
      }
      setCertificateFile(file);
      setError(null);
    }
  };

  const removeCertificate = () => {
    setCertificateFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadCertificate = async (): Promise<{ url: string; fileName: string } | null> => {
    if (!certificateFile) return null;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', certificateFile);
      formData.append('proId', proId);

      const response = await fetch(`${apiEndpoint}/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload certificate');
      }

      return {
        url: data.url,
        fileName: data.fileName,
      };
    } catch (err) {
      console.error('Error uploading certificate:', err);
      throw err;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!form.category) {
      setError('Category is required');
      return;
    }
    if (!form.activityDate) {
      setError('Activity date is required');
      return;
    }
    // Validate activity date is not in the future
    const activityDate = new Date(form.activityDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (activityDate > today) {
      setError('Activity date cannot be in the future');
      return;
    }
    // Validate activity date is not before CRPO registration date
    if (crpoRegistrationDate) {
      const regDate = new Date(crpoRegistrationDate);
      regDate.setHours(0, 0, 0, 0);
      if (activityDate < regDate) {
        setError('Activity date cannot be before your CRPO registration date');
        return;
      }
    }
    if (form.durationHours === 0 && form.durationMinutes === 0) {
      setError('Duration must be greater than 0');
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload certificate first if present
      let certificateData: { url: string; fileName: string } | null = null;
      if (certificateFile) {
        certificateData = await uploadCertificate();
      }

      // Calculate total duration in minutes
      const duration = form.durationHours * 60 + form.durationMinutes;

      // Submit activity
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          proId,
          title: form.title.trim(),
          category: form.category,
          activityDate: form.activityDate,
          duration,
          provider: form.provider.trim() || null,
          description: form.description.trim() || null,
          certificateUrl: certificateData?.url || null,
          certificateFileName: certificateData?.fileName || null,
          learningObjectives: form.learningObjectives.trim() || null,
          keyTakeaways: form.keyTakeaways.trim() || null,
          applicationPlan: form.applicationPlan.trim() || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create activity');
      }

      onSuccess();
    } catch (err) {
      console.error('Error creating activity:', err);
      setError(err instanceof Error ? err.message : 'Failed to create activity');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCategoryInfo = form.category ? CATEGORY_INFO[form.category] : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
          Activity Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="title"
          name="title"
          value={form.title}
          onChange={handleInputChange}
          placeholder="e.g., CBT Fundamentals Workshop"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
        />
      </div>

      {/* Category */}
      <div>
        <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
          Category <span className="text-red-500">*</span>
        </label>
        <select
          id="category"
          name="category"
          value={form.category}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
        >
          <option value="">Select a category</option>
          <optgroup label="Didactic (Instruction-based)">
            {DIDACTIC_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {CATEGORY_INFO[cat].label}
              </option>
            ))}
          </optgroup>
          <optgroup label="Experiential (Experience-based)">
            {EXPERIENTIAL_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {CATEGORY_INFO[cat].label}
              </option>
            ))}
          </optgroup>
          <optgroup label="Other">
            <option value="OTHER">{CATEGORY_INFO.OTHER.label}</option>
          </optgroup>
        </select>
        {selectedCategoryInfo && (
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                selectedCategoryInfo.type === 'DIDACTIC'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-green-100 text-green-700'
              }`}
            >
              {selectedCategoryInfo.type === 'DIDACTIC' ? (
                <BookOpen className="w-3 h-3" />
              ) : (
                <Users className="w-3 h-3" />
              )}
              {selectedCategoryInfo.type === 'DIDACTIC' ? 'Didactic' : 'Experiential'}
            </span>
            <span className="text-gray-500">{selectedCategoryInfo.description}</span>
          </div>
        )}
      </div>

      {/* Date and Duration */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="activityDate" className="block text-sm font-medium text-gray-700 mb-1">
            Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            id="activityDate"
            name="activityDate"
            value={form.activityDate}
            onChange={handleInputChange}
            min={crpoRegistrationDate?.split('T')[0] || undefined}
            max={new Date().toISOString().split('T')[0]}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Duration <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-2">
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="99"
                value={form.durationHours}
                onChange={(e) => handleNumberChange('durationHours', parseInt(e.target.value) || 0)}
                className="w-16 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center"
              />
              <span className="ml-1 text-gray-600">h</span>
            </div>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="59"
                step="15"
                value={form.durationMinutes}
                onChange={(e) => handleNumberChange('durationMinutes', parseInt(e.target.value) || 0)}
                className="w-16 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center"
              />
              <span className="ml-1 text-gray-600">m</span>
            </div>
          </div>
        </div>
      </div>

      {/* Provider */}
      <div>
        <label htmlFor="provider" className="block text-sm font-medium text-gray-700 mb-1">
          Provider / Organization
        </label>
        <input
          type="text"
          id="provider"
          name="provider"
          value={form.provider}
          onChange={handleInputChange}
          placeholder="e.g., CAMH, OAMHP, University of Toronto"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
        />
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={form.description}
          onChange={handleInputChange}
          rows={2}
          placeholder="Brief description of the activity..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
        />
      </div>

      {/* Certificate Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Certificate / Proof of Completion
        </label>
        {certificateFile ? (
          <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <FileText className="w-5 h-5 text-gray-500" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {certificateFile.name}
              </p>
              <p className="text-xs text-gray-500">
                {(certificateFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
            <button
              type="button"
              onClick={removeCertificate}
              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors"
          >
            <Upload className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-600">
              Click to upload PDF, image, or document
            </span>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          className="hidden"
        />
        <p className="mt-1 text-xs text-gray-500">Max 10MB. PDF, JPEG, PNG, or Word documents.</p>
      </div>

      {/* Learning Reflection (Collapsible) */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowReflection(!showReflection)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <span className="text-sm font-medium text-gray-700">
            Learning Reflection (Optional)
          </span>
          {showReflection ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </button>
        {showReflection && (
          <div className="p-4 space-y-4 border-t border-gray-200">
            <div>
              <label
                htmlFor="learningObjectives"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Learning Objectives
              </label>
              <textarea
                id="learningObjectives"
                name="learningObjectives"
                value={form.learningObjectives}
                onChange={handleInputChange}
                rows={2}
                placeholder="What were the main learning objectives?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
              />
            </div>
            <div>
              <label
                htmlFor="keyTakeaways"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Key Takeaways
              </label>
              <textarea
                id="keyTakeaways"
                name="keyTakeaways"
                value={form.keyTakeaways}
                onChange={handleInputChange}
                rows={2}
                placeholder="What were your key takeaways from this activity?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
              />
            </div>
            <div>
              <label
                htmlFor="applicationPlan"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Application Plan
              </label>
              <textarea
                id="applicationPlan"
                name="applicationPlan"
                value={form.applicationPlan}
                onChange={handleInputChange}
                rows={2}
                placeholder="How will you apply what you learned to your practice?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
              />
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="pt-4 border-t border-gray-200 space-y-3">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting || isUploading}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || isUploading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            {isSubmitting || isUploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {isUploading ? 'Uploading...' : 'Saving...'}
              </>
            ) : (
              'Add Activity'
            )}
          </button>
        </div>
      </div>
    </form>
  );
}
